import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  query as fsQuery,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../lib/useAuth";

const PAGE_SIZE = 20;

export default function AdminListings() {
  const { user } = useAuth();

  // 'pending' | 'published' | 'rejected' | 'removed'
  const [statusTab, setStatusTab] = useState("pending");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [endCursor, setEndCursor] = useState(null);
  const [queryText, setQueryText] = useState("");
  const [flash, setFlash] = useState("");

  const [rejectingId, setRejectingId] = useState(null);
  const rejectReasonRef = useRef(null);

  const [delistingListing, setDelistingListing] = useState(null);
  const delistReasonRef = useRef(null);

  // Load first page whenever filters change
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setRows([]);
      setEndCursor(null);
      try {
        const { docs, last } = await runQuery({
          after: null,
          statusTab,
          queryText,
        });
        if (!alive) return;
        setRows(docs);
        setEndCursor(last);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [statusTab, queryText]);

  async function loadMore() {
    if (!endCursor) return;
    setLoadingMore(true);
    try {
      const { docs, last } = await runQuery({
        after: endCursor,
        statusTab,
        queryText,
      });
      setRows((r) => [...r, ...docs]);
      setEndCursor(last);
    } finally {
      setLoadingMore(false);
    }
  }

  async function approve(id) {
    if (!window.confirm("Approve and publish this listing?")) return;
    await updateDoc(doc(db, "listings", id), {
      status: "published",
      reviewedAt: serverTimestamp(),
      reviewedBy: user?.uid || "admin",
      publishedAt: serverTimestamp(),
      rejectionReason: "",
      removalReason: "",
      removedAt: null,
      removedBy: null,
    });
    setFlash("Listing approved.");
    setRows((r) => r.filter((x) => x.id !== id));
    clearFlashSoon();
  }

  /* ========================= Reject flow ========================= */

  function openReject(id) {
    setRejectingId(id);
    setTimeout(() => rejectReasonRef.current?.focus(), 0);
  }

  function cancelReject() {
    setRejectingId(null);
  }

  async function submitReject() {
    const id = rejectingId;
    if (!id) return;
    const reason =
      rejectReasonRef.current?.value.trim() || "Not a fit for the marketplace";

    await updateDoc(doc(db, "listings", id), {
      status: "rejected",
      reviewedAt: serverTimestamp(),
      reviewedBy: user?.uid || "admin",
      rejectionReason: reason,
    });

    setRejectingId(null);
    setFlash("Listing rejected.");
    setRows((r) => r.filter((x) => x.id !== id));
    clearFlashSoon();
  }

  /* ========================= Delist / remove flow ========================= */

  function openDelist(listing) {
    setDelistingListing(listing);
    setTimeout(() => delistReasonRef.current?.focus(), 0);
  }

  function cancelDelist() {
    setDelistingListing(null);
  }

  async function submitDelist() {
    if (!delistingListing) return;

    const { id, ownerUid, ownerEmail, title } = delistingListing;
    const reason =
      delistReasonRef.current?.value.trim() ||
      "Listing removed from the marketplace by an administrator.";

    if (
      !window.confirm(
        "Delist / remove this listing? It will no longer appear to guests."
      )
    ) {
      return;
    }

    // Update listing document
    await updateDoc(doc(db, "listings", id), {
      status: "removed",
      removedAt: serverTimestamp(),
      removedBy: user?.uid || "admin",
      removalReason: reason,
    });

    // Fire a notification document that your backend can use
    await notifyListingRemoved({
      listingId: id,
      ownerUid: ownerUid || null,
      ownerEmail: ownerEmail || null,
      title: title || "",
      reason,
      removedBy: user?.uid || "admin",
    });

    setDelistingListing(null);
    setFlash("Listing removed / delisted.");
    setRows((r) => r.filter((x) => x.id !== id));
    clearFlashSoon();
  }

  function clearFlashSoon() {
    setTimeout(() => setFlash(""), 2000);
  }

  const hasMore = !!endCursor;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Listings</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Approve, reject, or delist listings from the marketplace.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-neutral-200 p-1 text-xs">
          <Tab
            label="Pending"
            active={statusTab === "pending"}
            onClick={() => setStatusTab("pending")}
          />
          <Tab
            label="Published"
            active={statusTab === "published"}
            onClick={() => setStatusTab("published")}
          />
          <Tab
            label="Rejected"
            active={statusTab === "rejected"}
            onClick={() => setStatusTab("rejected")}
          />
          <Tab
            label="Removed"
            active={statusTab === "removed"}
            onClick={() => setStatusTab("removed")}
          />
        </div>
      </header>

      <div className="flex items-center justify-between gap-3">
        <input
          value={queryText}
          onChange={(e) => setQueryText(e.target.value)}
          placeholder="Search title or city…"
          className="w-full max-w-xs rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
        />
        {flash && (
          <div className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs text-green-700">
            {flash}
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-neutral-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-neutral-50 text-neutral-600">
            <tr>
              <Th>Title</Th>
              <Th>City</Th>
              <Th>Price</Th>
              <Th>Photos</Th>
              <Th>Owner</Th>
              <Th>Submitted</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <Td colSpan={8}>
                  <div className="p-4 text-neutral-500">Loading…</div>
                </Td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <Td colSpan={8}>
                  <div className="p-4 text-neutral-500">No results.</div>
                </Td>
              </tr>
            ) : (
              rows.map((l) => (
                <tr key={l.id} className="border-t align-top">
                  <Td>
                    <div className="font-medium">
                      {l.title || "—"}
                    </div>
                    <div className="max-w-[320px] truncate text-xs text-neutral-500">
                      {l.summary || "—"}
                    </div>
                  </Td>
                  <Td>
                    {[l.city, l.stateCode].filter(Boolean).join(", ") || "—"}
                  </Td>
                  <Td>
                    {l.dayPrice ? fmtCurrency(Number(l.dayPrice)) : "—"}
                  </Td>
                  <Td>{Array.isArray(l.photos) ? l.photos.length : 0}</Td>
                  <Td className="text-xs">
                    <div>{l.ownerUid || "—"}</div>
                    {l.ownerEmail && (
                      <div className="text-neutral-500">
                        {l.ownerEmail}
                      </div>
                    )}
                  </Td>
                  <Td>{fmtDate(l.submittedAt)}</Td>
                  <Td>
                    <span className={chipClass(l.status)}>
                      {l.status || "—"}
                    </span>
                    {l.rejectionReason && (
                      <div className="mt-1 text-xs text-neutral-500">
                        Reason: {l.rejectionReason}
                      </div>
                    )}
                    {l.removalReason && (
                      <div className="mt-1 text-xs text-neutral-500">
                        Removed: {l.removalReason}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-2">
                      {l.status === "pending" && (
                        <>
                          <button
                            onClick={() => approve(l.id)}
                            className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white hover:bg-[#B8860B]"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => openReject(l.id)}
                            className="rounded-full border border-neutral-300 px-3 py-1 text-xs hover:border-neutral-400"
                          >
                            Reject
                          </button>
                        </>
                      )}

                      {l.status === "published" && (
                        <button
                          onClick={() => openDelist(l)}
                          className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs text-red-700 hover:border-red-300"
                        >
                          Delist / Remove
                        </button>
                      )}

                      <a
                        href={`/listing/${l.id}`}
                        className="rounded-full border border-neutral-300 px-3 py-1 text-xs hover:border-neutral-400"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open
                      </a>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded-full border border-neutral-300 px-4 py-2 text-sm hover:border-neutral-400 disabled:opacity-60"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      {rejectingId && (
        <RejectModal
          onCancel={cancelReject}
          onSubmit={submitReject}
          inputRef={rejectReasonRef}
        />
      )}

      {delistingListing && (
        <DelistModal
          listing={delistingListing}
          onCancel={cancelDelist}
          onSubmit={submitDelist}
          inputRef={delistReasonRef}
        />
      )}
    </div>
  );
}

/* ========================= UI helpers ========================= */

function Tab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 ${
        active ? "bg-black text-white" : ""
      }`}
    >
      {label}
    </button>
  );
}

function Th({ children }) {
  return <th className="py-2 px-3">{children}</th>;
}

function Td({ children, colSpan }) {
  return (
    <td className="py-3 px-3" colSpan={colSpan}>
      {children}
    </td>
  );
}

function chipClass(status) {
  const base =
    "inline-flex items-center rounded-full px-2 py-0.5 text-xs capitalize";
  if (status === "published")
    return `${base} bg-green-100 text-green-700`;
  if (status === "pending")
    return `${base} bg-amber-100 text-amber-800`;
  if (status === "rejected")
    return `${base} bg-red-100 text-red-700`;
  if (status === "removed")
    return `${base} bg-neutral-200 text-neutral-700`;
  return `${base} bg-neutral-100 text-neutral-700`;
}

/* ========================= Formatting helpers ========================= */

function fmtCurrency(n) {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : typeof ts === "number"
        ? new Date(ts)
        : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

/* ========================= Data layer ========================= */

async function runQuery({ after, statusTab, queryText }) {
  const colRef = collection(db, "listings");

  // Order removed listings by removedAt; others by submittedAt
  const orderField =
    statusTab === "removed" ? "removedAt" : "submittedAt";

  let q = fsQuery(
    colRef,
    where("status", "==", statusTab),
    orderBy(orderField, "desc"),
    limit(PAGE_SIZE)
  );

  if (after) {
    q = fsQuery(
      colRef,
      where("status", "==", statusTab),
      orderBy(orderField, "desc"),
      startAfter(after),
      limit(PAGE_SIZE)
    );
  }

  const snap = await getDocs(q);
  let docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Simple client-side filter
  if (queryText) {
    const t = queryText.toLowerCase();
    docs = docs.filter((d) => {
      const parts = [
        d.title || "",
        d.summary || "",
        d.city || "",
        d.stateCode || "",
      ]
        .join(" ")
        .toLowerCase();
      return parts.includes(t);
    });
  }

  const last = snap.docs[snap.docs.length - 1] || null;
  return { docs, last };
}

/* ========================= Modals ========================= */

function RejectModal({ onCancel, onSubmit, inputRef }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold">Reject listing</h3>
        <p className="mt-1 text-sm text-neutral-600">
          Add a short reason (host will see this).
        </p>
        <textarea
          ref={inputRef}
          rows={3}
          className="mt-3 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
          placeholder="Reason (e.g., low-quality photos, missing address)…"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-full px-3 py-1.5 text-sm hover:bg-neutral-100"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="rounded-full bg-black px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#B8860B]"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

function DelistModal({ listing, onCancel, onSubmit, inputRef }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold">Delist / remove listing</h3>
        <p className="mt-1 text-sm text-neutral-600">
          This listing will be removed from the guest-facing marketplace.
          The host will be notified.
        </p>
        <div className="mt-3 rounded-xl bg-neutral-50 p-3 text-xs text-neutral-700">
          <div className="font-medium">{listing.title || "Untitled"}</div>
          <div className="text-neutral-500">
            {[listing.city, listing.stateCode]
              .filter(Boolean)
              .join(", ") || "—"}
          </div>
        </div>
        <textarea
          ref={inputRef}
          rows={3}
          className="mt-3 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
          placeholder="Reason visible to the host (e.g., policy violation, quality issues)…"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-full px-3 py-1.5 text-sm hover:bg-neutral-100"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="rounded-full bg-red-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-700"
          >
            Delist listing
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================= Notification writer ========================= */
/**
 * Writes a notification doc that your backend can watch and send email /
 * push / in-app notifications from.
 */
async function notifyListingRemoved({
  listingId,
  ownerUid,
  ownerEmail,
  title,
  reason,
  removedBy,
}) {
  try {
    await addDoc(collection(db, "notifications"), {
      type: "listingRemoved",
      listingId,
      ownerUid,
      ownerEmail,
      title,
      reason,
      removedBy,
      createdAt: serverTimestamp(),
      unread: true,
      channel: "email", // hint for backend; adjust if needed
    });
  } catch (err) {
    console.error("Failed to create removal notification", err);
  }
}
